import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import { useTexte } from '../texte'
import { useEscape } from '../hooks/useEscape'
import {
  abonniereInstallation,
  fuehrtZurAnleitung,
  installationAnbieten,
  installationMoeglich,
} from '../lib/installieren'

/**
 * "Als App hinzufügen" (#101, 1.56.0).
 *
 * **Ein Knopf, zwei Ausgänge**, und welcher gilt, sagt der Browser: wo
 * Chromium sein Angebot gemacht hat, öffnet der Tipp den Systemdialog; wo
 * nicht, steht die Anleitung für Safari da. Ein Knopf, der nirgends
 * hinführt, steht gar nicht erst da (`installationMoeglich`).
 *
 * **Das Blatt hängt am `body`** wie jede Ebene obendrauf: das milchige
 * Material der Seite hielte sonst ein `position: fixed` fest (die Regel
 * steht an `blattlage.mjs`).
 */
export function Appknopf({ variante = 'karte' }: { variante?: 'karte' | 'zeile' }) {
  const texte = useTexte()
  const t = texte.app
  const [, neuZeichnen] = useState(0)
  const [anleitung, setAnleitung] = useState(false)
  useEscape(anleitung ? () => setAnleitung(false) : null)

  // Das Angebot des Browsers kommt, wann es will -- auch nach dem Zeichnen.
  useEffect(() => abonniereInstallation(() => neuZeichnen((n) => n + 1)), [])

  if (!installationMoeglich()) return null

  const tippen = async () => {
    if ((await installationAnbieten()) === 'anleitung') setAnleitung(true)
  }

  return (
    <>
      {variante === 'karte' ? (
        <div className="card card--seite" data-appknopf="karte">
          <h2 className="card__titel">{t.titel}</h2>
          <p className="card__lead card__lead--dicht">{t.lead}</p>
          <button type="button" className="button button--outline" onClick={tippen} data-appknopf-start="">
            {fuehrtZurAnleitung() ? t.knopfAnleitung : t.knopfInstallieren}
          </button>
        </div>
      ) : (
        <button type="button" className="appzeile" onClick={tippen} data-appknopf="zeile" data-appknopf-start="">
          <span className="appzeile__text">{t.zeile}</span>
        </button>
      )}

      {anleitung &&
        createPortal(
          <div
            className="rueckfrage"
            role="dialog"
            aria-modal="true"
            aria-label={t.anleitungTitel}
            data-appanleitung=""
            /* **Der Schleier schließt** (1.66.0, aus der Codereview). Er hatte
               keinen Handler, während das Blatt darin `stopPropagation()` rief
               -- und das ist nur sinnvoll, wenn es etwas anzuhalten gibt.
               Antippen daneben tat also nichts, anders als bei jeder anderen
               Rückfrage. Drei Zeilen gehören ins Blatt und nicht zum Aufrufer:
               Portal, Escape, Schleier (1.63.0); hier fehlte die dritte. */
            onClick={() => setAnleitung(false)}
          >
            {/* Dasselbe Blatt wie bei jeder Rückfrage, damit es sich anfühlt
                wie alles andere: die Frage oben, die Schritte darunter, ein
                Knopf zum Schließen. */}
            <div className="rueckfrage__blatt" onClick={(event) => event.stopPropagation()}>
              <p className="rueckfrage__frage">{t.anleitungTitel}</p>
              <ol className="appanleitung">
                {t.schritte.map((schritt) => (
                  <li key={schritt}>{schritt}</li>
                ))}
              </ol>
              <p className="feld__hinweis">{t.nurSafari}</p>
              <div className="rueckfrage__knoepfe">
                <button type="button" className="button button--small" onClick={() => setAnleitung(false)}>
                  {texte.allgemein.schliessen}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}
